/**
 * Shared template-merge helper: replaces `{{key}}` placeholders in a string
 * with values from a vars map. A placeholder with no entry in the map is left
 * untouched (only keys present in the map are rewritten).
 *
 * Extracted from ContactEmailComposer.jsx and MeetingActionModal.jsx, which
 * carried byte-identical copies (audit: dedup / drift risk). Template merge
 * vars are the contact/account fields ({{contact_first_name}}, {{company_name}},
 * …) — distinct from the sender/signature vars handled by
 * lib/signatureMergeVars.js, which is why this is a separate helper.
 *
 * `str` is treated as plain text: the split/join replacement is literal, so
 * values are never interpreted as regex or re-escaped. Callers that feed the
 * result into an HTML editor are responsible for sanitizing the *source*
 * values (DOMPurify) if they originate from untrusted input.
 */
export function applyMerge(str, vars) {
    if (!str) return '';
    return Object.entries(vars || {}).reduce(
        (acc, [k, v]) => acc.split(`{{${k}}}`).join(v || ''),
        str
    );
}

/**
 * The contact/account merge map for USER-AUTHORED templates.
 *
 * Two merge vocabularies exist in this codebase and they must not be confused:
 *
 *  - `applyMerge` above takes BARE snake_case keys and is correct for callers
 *    that own both sides — MeetingActionModal pairs it with its own hardcoded
 *    `"Hi {{contact_first_name}}, …"` string, so that map matches its template.
 *  - Anything a USER authored — an email template, a body typed with the
 *    MergeVariableDropdown — speaks the PascalCase vocabulary that
 *    `components/MergeVariableDropdown.jsx` inserts and that the backend
 *    resolver `backend/utils/merge_fields.py` implements
 *    (`CONTACT_FIELDS_MAP` / `ACCOUNT_FIELDS_MAP`).
 *
 * ContactEmailComposer applied the snake_case map to user templates, so it
 * matched NOTHING: `applyMerge` leaves an unknown placeholder untouched by
 * design, and the contact-composer send lane resolved nothing server-side
 * either, so recipients received a literal "Hi {{FirstName}},". Observed on
 * aicoaches-dev-2 (comm_id fcf27f49 stored every token unresolved).
 *
 * Keys are BRACED here, matching `lib/signatureMergeVars.js`, so the two maps
 * compose in one pass through `resolveMergeVars`.
 *
 * Field names mirror the backend resolvers rather than guessing: `Email` and
 * `Title` fall back to the `contact_*` aliases the backend accepts, and
 * `CompanyName` takes the account name.
 */
export function buildContactMergeMap(contact, account) {
    const c = contact || {};
    const a = account || {};
    const firstName = c.first_name
        || (c.contact_name || c.name || '').split(' ')[0]
        || '';
    const lastName = c.last_name
        || (c.contact_name || c.name || '').split(' ').slice(1).join(' ')
        || '';
    return {
        '{{FirstName}}': firstName,
        '{{LastName}}': lastName,
        '{{Email}}': c.email || c.contact_email || '',
        '{{Title}}': c.title || c.contact_title || '',
        '{{Phone}}': c.phone || '',
        '{{Department}}': c.department || '',
        '{{CompanyName}}': a.name || '',
        '{{Industry}}': a.industry || '',
        '{{Website}}': a.website || '',
        '{{City}}': a.city || c.city || '',
        '{{Country}}': a.country || '',
    };
}

/**
 * Apply a BRACED merge map (keys already include the `{{ }}`).
 *
 * Literal split/join, so a value is never interpreted as a regex. An
 * unmatched placeholder is left in place — the caller decides whether that is
 * acceptable; for anything being SENT it is not, which is why the send lane
 * resolves server-side as well.
 */
export function resolveMergeVars(text, map) {
    if (!text) return text;
    let out = text;
    for (const [key, val] of Object.entries(map || {})) {
        out = out.split(key).join(val || '');
    }
    return out;
}
